import { createFileRoute, Link, useRouter, notFound } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { getBrewerySite, type BrewerySiteDetail } from "@/lib/brewery-site.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Building2, ExternalLink, Beer } from "lucide-react";
import { BackButton } from "@/components/BackButton";

const siteQuery = (id: string) =>
  queryOptions({
    queryKey: ["brewery-site", id],
    queryFn: () => getBrewerySite({ data: { id } }),
  });

export const Route = createFileRoute("/provozovna/$id")({
  loader: async ({ params, context }) => {
    console.log("[/provozovna/$id loader] start", { id: params.id });
    try {
      const data = await context.queryClient.ensureQueryData(siteQuery(params.id));
      console.log("[/provozovna/$id loader] loaded", { id: params.id, found: !!data });
      if (!data) throw notFound();
      return data;
    } catch (e) {
      console.error("[/provozovna/$id loader] error", e);
      throw e;
    }
  },

  head: ({ params, loaderData }) => {
    const url = `https://cerveza.international/provozovna/${params.id}`;
    if (!loaderData) {
      return {
        meta: [
          { title: "Provozovna nenalezena — Cerveza.International" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const d = loaderData as BrewerySiteDetail;
    const cityStr = d.city?.name ? `, ${d.city.name}` : "";
    const title = `${d.official_name}${cityStr} — Provozovna | Cerveza.International`;
    const desc =
      [
        d.brewery?.name && `Provozovna pivovaru ${d.brewery.name}`,
        d.city?.name,
        d.country?.name,
        d.address_line_1,
      ]
        .filter(Boolean)
        .join(", ") || d.official_name;
    // No per-place photo in loader; omit og:image so hosting/root fallback
    // supplies the site preview instead of a generic placeholder.
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Cerveza.International" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FoodEstablishment",
          name: d.official_name,
          address: {
            "@type": "PostalAddress",
            streetAddress: d.address_line_1 ?? undefined,
            postalCode: d.postal_code ?? undefined,
            addressLocality: d.city?.name ?? undefined,
            addressRegion: d.admin_area?.name ?? undefined,
            addressCountry: d.country?.iso2 ?? d.country?.name ?? undefined,
          },
          geo: d.latitude != null && d.longitude != null ? {
            "@type": "GeoCoordinates",
            latitude: d.latitude,
            longitude: d.longitude,
          } : undefined,
          url,
        }),
      }],
    };
  },

  component: BrewerySitePage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">Chyba</h1>
        <p className="mb-4 text-muted-foreground">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Zkusit znovu</Button>
      </div>
    );
  },
  notFoundComponent: () => {
    const { id } = Route.useParams();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-terracotta">
          404
        </div>
        <h1 className="mb-3 font-serif text-3xl font-bold">Provozovna nenalezena</h1>
        <p className="mb-2 text-muted-foreground">
          Provozovnu s tímto identifikátorem se nepodařilo najít nebo již není publikovaná.
        </p>
        <p className="mb-6 break-all font-mono text-xs text-muted-foreground">ID: {id}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/mapa">← Zpět na mapu</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/">Domů</Link>
          </Button>
        </div>
      </div>
    );
  },
});


function BrewerySitePage() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(siteQuery(id));
  if (!data) return null;
  const d = data;

  const addressLines = [
    d.address_line_1,
    [d.postal_code, d.city?.name].filter(Boolean).join(" "),
    [d.admin_area?.name, d.country?.name].filter(Boolean).join(", "),
  ].filter((x): x is string => !!x && x.trim().length > 0);

  const hasCoords = d.latitude != null && d.longitude != null;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps/search/?api=1&query=${d.latitude},${d.longitude}`
    : null;
  const osmEmbed = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${d.longitude! - 0.01}%2C${d.latitude! - 0.005}%2C${d.longitude! + 0.01}%2C${d.latitude! + 0.005}&layer=mapnik&marker=${d.latitude}%2C${d.longitude}`
    : null;

  const features = [
    d.taproom && "Nálevna",
    d.restaurant && "Restaurace",
    d.visitor_center && "Návštěvnické centrum",
    d.tours_available && "Prohlídky",
    d.company_shop && "Podniková prodejna",
  ].filter(Boolean) as string[];

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8">
      <BackButton />
      <div className="mb-6 mt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-brand-terracotta">
          Provozovna {d.site_type ? `· ${d.site_type}` : ""}
        </div>
        <h1 className="font-serif text-3xl font-bold md:text-4xl">{d.official_name}</h1>
        {d.site_status && (
          <Badge variant="secondary" className="mt-2">{d.site_status}</Badge>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-3 flex items-center gap-2 font-semibold">
            <MapPin className="h-4 w-4" /> Adresa
          </h2>
          {addressLines.length > 0 ? (
            <address className="not-italic text-sm leading-relaxed text-muted-foreground">
              {addressLines.map((l, i) => <div key={i}>{l}</div>)}
            </address>
          ) : (
            <p className="text-sm text-muted-foreground">Adresa neuvedena</p>
          )}
          {hasCoords && (
            <div className="mt-3 text-xs text-muted-foreground">
              GPS: {d.latitude!.toFixed(6)}, {d.longitude!.toFixed(6)}
            </div>
          )}
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-sm text-brand-terracotta underline"
            >
              Otevřít v mapách <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-3 flex items-center gap-2 font-semibold">
            <Building2 className="h-4 w-4" /> Souvislosti
          </h2>
          <dl className="space-y-2 text-sm">
            {d.brewery && (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Pivovar</dt>
                <dd className="flex items-center gap-1 font-medium">
                  <Beer className="h-3.5 w-3.5" /> {d.brewery.name}
                </dd>
              </div>
            )}
            {d.operator && (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Provozovatel</dt>
                <dd className="font-medium">{d.operator.name}</dd>
              </div>
            )}
            {d.city && (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Město</dt>
                <dd className="font-medium">{d.city.name}</dd>
              </div>
            )}
            {d.country && (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Země</dt>
                <dd className="font-medium">
                  {d.country.name}{d.country.iso2 ? ` (${d.country.iso2})` : ""}
                </dd>
              </div>
            )}
            {(d.opened_year || d.closed_year) && (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Období</dt>
                <dd className="font-medium">
                  {d.opened_year ?? "?"}{d.closed_year ? ` – ${d.closed_year}` : ""}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      </div>

      {(d.brewery?.website_url) && (
        <Card className="mt-4 p-5">
          <h2 className="mb-3 flex items-center gap-2 font-semibold">
            <ExternalLink className="h-4 w-4" /> Kontakty a odkazy
          </h2>
          <ul className="space-y-1.5 text-sm">
            {d.brewery?.website_url && (
              <li>
                <a
                  href={d.brewery.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-terracotta underline"
                >
                  Web pivovaru {d.brewery.name} <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            )}
          </ul>
        </Card>
      )}

      {features.length > 0 && (
        <Card className="mt-4 p-5">
          <h2 className="mb-3 font-semibold">Vybavení a služby</h2>
          <div className="flex flex-wrap gap-2">
            {features.map((f) => <Badge key={f} variant="outline">{f}</Badge>)}
          </div>
        </Card>
      )}

      {osmEmbed && (
        <Card className="mt-4 overflow-hidden p-0">
          <iframe
            title="Mapa"
            src={osmEmbed}
            className="h-72 w-full border-0"
            loading="lazy"
          />
        </Card>
      )}

      <div className="mt-6">
        <Link to="/mapa" className="text-sm text-brand-terracotta underline">← Zpět na mapu</Link>
      </div>
    </div>
  );
}
